import React, { useContext } from 'react';
import classnames from 'classnames';
import { PapiGallection } from '@wix/da-papi-types';
import { withTranslation, WithTranslation } from 'react-i18next';
import Flex from '@wix/da-ds/pkg/Flex';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { normalizePremiumFolderData } from '../../../utils/normalizePremiumFolderData';
import FolderStats from '../../legos/FolderStats';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './FolderCardFooter.scss';
import parent_s from '../../FolderCard.scss';

export type Props = {
  folder: PapiGallection;
  className?: string;
  children?: React.ReactNode;
  isLocked?: boolean;
  showCountsWithoutCaptions?: boolean;
  withPrice?: boolean;
  oneLineDescription?: boolean;
  hideStats?: boolean;
};

const FolderCardFooter: React.FC<Props & WithTranslation> = ({
  folder,
  className,
  isLocked,
  withPrice,
  oneLineDescription,
  showCountsWithoutCaptions,
  hideStats,
  children,
  t,
}) => {
  const { formattedDollarPrice } = normalizePremiumFolderData(
    folder.premiumFolderData
  );

  const isMobile = useContext(MobileContext);
  const isPaid = folder.premiumFolderData?.type === 'paid';
  const withSubscribersCount = isPaid;
  const folderSize = folder.totalItemCount ?? folder.size ?? 0;

  return (
    <div
      className={classnames(
        s['root'],
        isMobile && s['mobile'],
        oneLineDescription && s['one-line-description'],
        !hideStats && s['with-stats'],
        className
      )}
    >
      <div className={s['description']}>
        <div className={s['name']}>
          <Heading
            className={classnames(s['folder-name'], parent_s['folder-name'])}
            title={folder.name}
          >
            {folder.name}
          </Heading>
          {isMobile && <span className={s['mobile-count']}>{folderSize}</span>}
          {isLocked && (
            <SimpleTooltip text={t('folderCard.locked')} usePortal>
              <LockIcon
                className={s['lock-icon']}
                size={IconSize.SMALLER}
                condensed
              />
            </SimpleTooltip>
          )}
          {withPrice && isPaid && (
            <span className={s['price']}>{formattedDollarPrice}</span>
          )}
        </div>
        <Flex>
          {!hideStats && (
            <FolderStats
              folder={folder}
              withSubscribersCount={withSubscribersCount}
              showCountsWithoutCaptions={showCountsWithoutCaptions}
            />
          )}
        </Flex>
      </div>
      {children}
    </div>
  );
};

export default withTranslation()(
  FolderCardFooter
) as React.ComponentType<Props>;
